/**
 * The C4 engine RPC contract: ONE immutable {@link PlaybackSnapshot} stream is the engine's
 * only notification path — state, status, errors and download progress all arrive as
 * snapshots (no positional listener tuples).
 *
 *  - `seq` is monotonic per engine instance: the worker handle drops out-of-order Comlink
 *    deliveries so consumers can never observe time running backwards.
 *  - `queueId` changes iff the queue's content identity changed; `queue` itself is included
 *    only in snapshots where it changed (and in the subscribe replay) — the broadcast diet
 *    that lets repeated status updates cross the worker boundary without re-cloning the
 *    queue.
 *  - `error` is a one-shot field: it is non-null exactly on the snapshot that surfaces the
 *    failure (codes `TTS_*`, C10 alignment) and null again on the next publish.
 *
 * This module is worker-importable: type-only imports plus one pure function.
 */
import type { TTSQueueItem } from '~types/tts';
import type { TTSVoice } from '../providers/types';
import type { FlightRecorderExport } from '../TTSFlightRecorder';

export type { FlightRecorderExport };

/** The possible states of TTS playback. */
export type TTSStatus = 'playing' | 'paused' | 'stopped' | 'loading' | 'completed';

/** Voice-data download progress (Piper). */
export interface DownloadInfo {
    voiceId: string;
    percent: number;
    status: string;
}

/** A playback error surfaced through the snapshot stream (C10 `TTS_*` codes). */
export interface PlaybackError {
    readonly code: string;
    readonly message: string;
}

/** The single immutable broadcast shape of the engine. */
export interface PlaybackSnapshot {
    /** Monotonic, engine-side; staleness detection across the worker boundary. */
    readonly seq: number;
    readonly status: TTSStatus;
    /** Changes iff queue content identity changes (the broadcast diet above). */
    readonly queueId: string;
    /**
     * Included only when `queueId` changed since the previous published snapshot
     * (and on subscribe replay); omitted otherwise — consumers keep their cached
     * queue for the same `queueId`.
     */
    readonly queue?: ReadonlyArray<TTSQueueItem>;
    readonly index: number;
    readonly sectionIndex: number;
    readonly activeCfi: string | null;
    /** Non-null exactly on the publish that surfaces a failure. */
    readonly error: PlaybackError | null;
    /** Non-null exactly on download-progress publishes. */
    readonly download: DownloadInfo | null;
}

export type SnapshotListener = (snapshot: PlaybackSnapshot) => void;

/**
 * The one tested derivation of "the UI should look like audio is active" — an
 * explicit selector rather than an inline "treat loading as playing" rule in the
 * store mirror (pinned by TtsController.test.ts's flicker guard):
 *  - 'loading' counts as playing to prevent play/pause button flicker during
 *    transitions between sentences or while buffering;
 *  - 'completed' counts as playing to keep background audio and the immersive
 *    UI active after the final sentence.
 */
export function isAudiblePlayback(status: TTSStatus): boolean {
    return status === 'playing' || status === 'loading' || status === 'completed';
}

/**
 * The public engine surface the app talks to — a STANDALONE interface, not a `Pick`
 * of the engine class. Both `PlaybackController` (the worker-resident engine) and the
 * main-thread `WorkerEngineHandle` implement it, so the app (via `getAudioPlayer()`)
 * depends only on this interface.
 *
 * Commands are ACKs: a resolved promise means the command was accepted (enqueued),
 * not completed — results flow exclusively through the snapshot stream.
 */
export interface TtsEngine {
    readonly engineName: string;

    // --- Playback control ---
    play(): Promise<void>;
    pause(): Promise<void> | void;
    stop(): Promise<void> | void;
    preview(text: string): Promise<void>;
    setSpeed(speed: number): Promise<void> | void;
    setVoice(voiceId: string): Promise<void> | void;
    setLanguage(lang: string): void;
    setProviderById(providerId: string): Promise<void> | void;
    setPrerollEnabled(enabled: boolean): void;
    setBackgroundAudioMode(mode: 'silence' | 'noise' | 'off'): void;
    setBackgroundVolume(volume: number): void;

    // --- Navigation ---
    /**
     * Switch the active book. The engine performs the context switch (epoch
     * bump) synchronously and runs the stop/reset as a sequenced task; the
     * in-process engine returns that task's promise, the worker handle stays
     * fire-and-forget.
     */
    setBookId(bookId: string | null): Promise<void> | void;
    loadSection(sectionIndex: number, autoPlay?: boolean): Promise<boolean | void>;
    loadSectionBySectionId(sectionId: string, autoPlay?: boolean, title?: string): Promise<void>;
    jumpTo(index: number): Promise<void> | void;
    seek(offset: number): Promise<void> | void;
    /**
     * Seek to an ABSOLUTE position (seconds) within the current section's queue
     * timeline — the time domain reported via `setPositionState`
     * ({@link QueueModel.getTotalDuration} / `getCurrentPosition`). Distinct from
     * {@link seek}, whose `offset` only carries a sign (discrete sentence/chapter
     * navigation, magnitude ignored). The OS media-notification scrubber is the
     * consumer: lock-screen drag -> native `seekto` -> here.
     */
    seekTo(time: number): Promise<void> | void;
    skipToNextSection(): Promise<boolean>;
    skipToPreviousSection(): Promise<boolean>;

    // --- Voices / init ---
    init(): Promise<void>;
    getVoices(): Promise<TTSVoice[]>;
    downloadVoice(voiceId: string): Promise<void>;
    deleteVoice(voiceId: string): Promise<void>;
    isVoiceDownloaded(voiceId: string): Promise<boolean>;

    // --- Diagnostics (the engine runs in the worker, whose flight
    // recorder is a different module instance than the main thread's —
    // DiagnosticsTab reads the LIVE engine buffer through the handle) ---
    /** The engine-side flight recorder's live ring buffer + stats. */
    exportDiagnostics(): Promise<FlightRecorderExport>;
    /** Freeze the engine-side ring buffer into a persisted snapshot; resolves its id. */
    triggerDiagnosticsSnapshot(trigger: string, note?: string): Promise<string | null>;

    // --- The snapshot stream ---
    /** Subscribe to playback snapshots; the latest snapshot (with queue) replays on the next tick. */
    subscribe(listener: SnapshotListener): () => void;
    /** The latest snapshot, synchronously (always carries the queue). */
    snapshot(): PlaybackSnapshot;
    /** Resolves when the engine is ready to accept commands. */
    whenReady(): Promise<void>;
}
